import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import {
  blogApi,
  ListBlogPostsParams,
  PublishBlogInput,
  PaginatedBlogPosts,
  BlogPostWithRelations,
  PullFromDocInput,
  GenerateSocialFromBlogInput,
  GenerateSocialFromBlogResult,
  BlogAutoGenerateSetting,
  UpdateBlogOnWpInput,
  UpdateBlogOnWpResult,
  PaginatedTeamMediaImages,
  UploadedFeaturedImage,
} from '@/services/blog.api';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { teamQueryKeys } from '@/lib/queryKeys';

/**
 * React Query hooks for the Blog feature (WordPress publishing, Phase 1).
 * Team-scoped so switching teams (God Mode) triggers an automatic refetch.
 */

export const BLOG_KEYS = {
  all: (teamId: number | null) => [...teamQueryKeys.all(teamId), 'blog'] as const,
  list: (teamId: number | null, params?: ListBlogPostsParams) =>
    [...BLOG_KEYS.all(teamId), 'list', params] as const,
  detail: (teamId: number | null, blogPostId: number) =>
    [...BLOG_KEYS.all(teamId), 'detail', blogPostId] as const,
  autoGenerateSetting: (teamId: number | null) =>
    [...BLOG_KEYS.all(teamId), 'auto-generate-setting'] as const,
  mediaImages: (teamId: number | null) =>
    [...BLOG_KEYS.all(teamId), 'media-images'] as const,
};

/**
 * GET /api/blog — paginated list, optionally filtered by status.
 */
export function useBlogPosts(params?: ListBlogPostsParams) {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery<PaginatedBlogPosts>({
    queryKey: BLOG_KEYS.list(teamId, params),
    queryFn: () => blogApi.list(params),
    enabled: hasTeam,
    staleTime: 30 * 1000,
  });
}

/**
 * GET /api/blog/:id — full detail with images/channel/featuredMedia, used
 * by the preview/confirm screen.
 *
 * The endpoint does a server-side throttled lazy status re-sync against
 * WordPress (e.g. a SCHEDULED post WP has since auto-published comes back
 * as PUBLISHED) and persists the reconciled row — but that reconciliation
 * only ever lands in THIS detail's cache entry via the normal `useQuery`
 * write. Once loaded, patch the matching row in any cached list(s) so
 * navigating back to the blog list shows the same (reconciled) status
 * instead of the stale one it was mounted with — no extra round-trip.
 */
export function useBlogPost(blogPostId: number | undefined) {
  const { teamId, hasTeam } = useEffectiveTeamId();
  const queryClient = useQueryClient();

  const query = useQuery<BlogPostWithRelations>({
    queryKey: BLOG_KEYS.detail(teamId, blogPostId ?? 0),
    queryFn: () => blogApi.getById(blogPostId as number),
    enabled: hasTeam && !!blogPostId,
    staleTime: 10 * 1000,
  });

  const detail = query.data;
  useEffect(() => {
    if (!detail) return;

    const blogNamespace = BLOG_KEYS.all(teamId);
    // `blogNamespace` is a shared prefix of both `list` and `detail` keys —
    // narrow to `list` entries only via the discriminator segment right
    // after the prefix, so we never run the `{ items }` patch against a
    // `BlogPostWithRelations` detail cache entry.
    const listSegmentIndex = blogNamespace.length;

    // Iterate + conditionally `setQueryData` per match instead of a single
    // `setQueriesData` — the latter commits a cache write (resetting
    // `dataUpdatedAt` and clearing any pending `isInvalidated` flag) on
    // EVERY matched query, even when the updater returns `old` unchanged.
    // That silently wiped invalidations queued by other actions on lists
    // this effect wasn't actually changing. Only write caches that
    // genuinely change.
    const matches = queryClient.getQueryCache().findAll({
      queryKey: blogNamespace,
      predicate: (cachedQuery) => cachedQuery.queryKey[listSegmentIndex] === 'list',
    });

    for (const match of matches) {
      const old = queryClient.getQueryData<PaginatedBlogPosts>(match.queryKey);
      if (!old) continue;

      const index = old.items.findIndex((item) => item.id === detail.id);
      if (index === -1) continue;

      const current = old.items[index];
      const isUnchanged =
        current.status === detail.status &&
        current.wpStatus === detail.wpStatus &&
        current.wpStatusCheckedAt === detail.wpStatusCheckedAt &&
        current.publishedAt === detail.publishedAt &&
        current.scheduledAt === detail.scheduledAt &&
        current.wpPostUrl === detail.wpPostUrl;
      if (isUnchanged) continue;

      const items = [...old.items];
      items[index] = {
        ...current,
        status: detail.status,
        wpStatus: detail.wpStatus,
        wpStatusCheckedAt: detail.wpStatusCheckedAt,
        publishedAt: detail.publishedAt,
        scheduledAt: detail.scheduledAt,
        wpPostUrl: detail.wpPostUrl,
      };
      queryClient.setQueryData<PaginatedBlogPosts>(match.queryKey, { ...old, items });
    }
    // Only re-run when the detail payload (or team) actually changes —
    // not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, teamId, queryClient]);

  return query;
}

/**
 * POST /api/blog/:id/sync-status — forces a live re-check of the blog
 * against its WordPress post (explicit "Refresh status" action; the detail
 * screen already does a throttled 60s lazy re-sync on load). Writes the
 * fresh detail DTO straight into the detail cache and invalidates the list
 * so the updated status/pill shows everywhere without an extra round-trip.
 */
export function useSyncBlogStatus(blogPostId: number) {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation<BlogPostWithRelations, unknown, void>({
    mutationFn: () => blogApi.syncStatus(blogPostId),
    onSuccess: (data) => {
      queryClient.setQueryData(BLOG_KEYS.detail(teamId, blogPostId), data);
      // Broad invalidate (not just the specific `list` key with params) so
      // any paginated/filtered list view currently mounted also refetches.
      queryClient.invalidateQueries({ queryKey: BLOG_KEYS.all(teamId) });
    },
  });
}

/**
 * POST /api/blog/pull — pulls a Google Doc into a new draft BlogPost.
 * Loading state can take several seconds (Doc fetch + image downloads +
 * uploads to S3) — callers should show a dedicated "pulling..." state.
 */
export function usePullBlogPost() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (input: PullFromDocInput) => blogApi.pull(input),
    onSuccess: () => {
      // refetchType: 'all' (not the default 'active') because the user is on
      // the pull/detail screen, not the list, so the list query is inactive —
      // a default invalidation only flags it stale. useBlogPost's mount effect
      // then does setQueriesData(...) across cached list entries to patch
      // statuses, which resets dataUpdatedAt and clears the pending
      // invalidation flag even when the list content is unchanged. By the time
      // the user navigates back to the list, the invalidation is gone and
      // React Query never refetches, so the freshly-imported draft is missing
      // until a hard refresh. Forcing an immediate refetch here closes that gap.
      queryClient.invalidateQueries({ queryKey: BLOG_KEYS.all(teamId), refetchType: 'all' });
      // Server-side triggers a BLOG_EXCERPT generation against the team's
      // DEFAULT brand profile — invalidate so its usage count/last-used
      // updates live without a manual refresh.
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
    },
  });
}

/**
 * POST /api/blog/:id/publish — publish / save-draft / schedule to
 * WordPress. Safe to retry after a FAILED status (server-side dupe guard).
 */
export function usePublishBlogPost(blogPostId: number) {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (input: PublishBlogInput) => blogApi.publish(blogPostId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOG_KEYS.all(teamId) });
    },
  });
}

/**
 * POST /api/blog/:id/update-wp — Phase 3 edit-in-place: pushes
 * title/excerpt/slug/categories/tags/featured-image (and optionally a fresh
 * re-pull of the source Google Doc) to the blog's ALREADY-published
 * WordPress post. Invalidates the blog detail/list AND the team's posts
 * cache, since a `refreshFromDoc` re-pull can surface newly-added images
 * that feed into later social drafts.
 */
export function useUpdateBlogOnWp(blogPostId: number) {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation<UpdateBlogOnWpResult, unknown, UpdateBlogOnWpInput>({
    mutationFn: (input) => blogApi.updateOnWp(blogPostId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOG_KEYS.all(teamId) });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.posts.all(teamId) });
    },
  });
}

/**
 * DELETE /api/blog/:id — soft-delete only.
 */
export function useDeleteBlogPost() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (blogPostId: number) => blogApi.remove(blogPostId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOG_KEYS.all(teamId) });
    },
  });
}

/**
 * POST /api/blog/:id/generate-social — Phase 2 (SPEC.md decision C).
 * AI-generates social post drafts from a published/scheduled blog post.
 * Can take ~10-20s — callers must show a dedicated loading state, not a
 * generic spinner that looks stuck. Invalidates the team's posts cache
 * (list/calendar) so the newly-created drafts show up immediately.
 */
export function useGenerateSocialFromBlog(blogPostId: number) {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation<GenerateSocialFromBlogResult, unknown, GenerateSocialFromBlogInput>({
    mutationFn: (input) => blogApi.generateSocial(blogPostId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.posts.all(teamId) });
      // Applies the team's DEFAULT brand profile server-side — invalidate so
      // its usage count/last-used updates live without a manual refresh.
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
    },
  });
}

/**
 * GET /api/blog/settings/auto-generate — the team's "auto-generate social
 * drafts when I publish a blog" toggle (SPEC.md decision C).
 */
export function useBlogAutoGenerateSetting() {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery<BlogAutoGenerateSetting>({
    queryKey: BLOG_KEYS.autoGenerateSetting(teamId),
    queryFn: () => blogApi.getAutoGenerateSetting(),
    enabled: hasTeam,
    staleTime: 60 * 1000,
  });
}

/**
 * PUT /api/blog/settings/auto-generate
 */
export function useUpdateBlogAutoGenerateSetting() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation<BlogAutoGenerateSetting, unknown, boolean>({
    mutationFn: (enabled) => blogApi.updateAutoGenerateSetting(enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(BLOG_KEYS.autoGenerateSetting(teamId), data);
    },
  });
}

/**
 * GET /api/blog/media-images — infinite-scroll over the team's `media`-table
 * images (Canva imports + blog images across the team's posts), for the
 * featured-image "From library" picker (`BlogFeaturedImageLibraryModal`).
 * Distinct source from `useMediaLibraryInfinite` (post_media table) — do not
 * conflate the two caches.
 */
export function useBlogMediaImages(limit = 24) {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useInfiniteQuery<PaginatedTeamMediaImages>({
    queryKey: [...BLOG_KEYS.mediaImages(teamId), limit],
    queryFn: ({ pageParam }) => blogApi.getMediaImages({ page: pageParam as number, limit }),
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: hasTeam,
    staleTime: 30 * 1000,
  });
}

/** Flattens the `useBlogMediaImages` infinite pages into a single item array. */
export function flattenBlogMediaImages(
  data: InfiniteData<PaginatedTeamMediaImages> | undefined
) {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

/**
 * POST /api/blog/featured-image/upload — uploads a new image to use as a
 * blog post's featured image. Does not touch any blog/list cache (the
 * caller wires the returned `{ id, url }` straight into the pre-publish/
 * update form state via `FeaturedImagePicker`'s `onSelect`).
 */
export function useUploadBlogFeaturedImage() {
  return useMutation<UploadedFeaturedImage, unknown, File>({
    mutationFn: (file) => blogApi.uploadFeaturedImage(file),
  });
}
